"use client";

import { c as _c } from "react/compiler-runtime";
import { getStepPercent } from "./progress-stepper";

// Step state configuration type
interface StepStateConfig {
  circleClasses: string;
  titleClasses: string;
  circleStyle: {
    backgroundColor: string;
    borderColor: string;
    color: string;
  };
  titleColor: string;
  statusText?: string;
  showProgressBar?: boolean;
}

// Step state configuration - prime candidate for JSON-based configuration
const STEP_STATE_CONFIG: Record<string, StepStateConfig> = {
  completed: {
    circleClasses: "border-2",
    titleClasses: "font-medium",
    circleStyle: {
      backgroundColor: "rgb(76, 175, 80)",
      borderColor: "rgb(255, 255, 255)",
      color: "rgb(255, 255, 255)"
    },
    titleColor: "rgb(55, 65, 81)"
  },
  "current-required-done": {
    circleClasses: "border-2",
    titleClasses: "font-medium",
    circleStyle: {
      backgroundColor: "rgb(139, 195, 74)",
      borderColor: "rgb(255, 255, 255)",
      color: "rgb(255, 255, 255)"
    },
    statusText: "Required Complete",
    showProgressBar: true,
    titleColor: "rgb(55, 65, 81)"
  },
  "current-incomplete": {
    circleClasses: "border-2",
    titleClasses: "font-medium",
    circleStyle: {
      backgroundColor: "rgb(255, 152, 0)",
      borderColor: "rgb(255, 255, 255)",
      color: "rgb(255, 255, 255)"
    },
    statusText: "In Progress",
    showProgressBar: true,
    titleColor: "rgb(55, 65, 81)"
  },
  "touched-required-done": {
    circleClasses: "border-2",
    titleClasses: "font-medium",
    circleStyle: {
      backgroundColor: "rgb(232, 245, 233)",
      borderColor: "rgb(200, 230, 201)",
      color: "rgb(56, 142, 60)"
    },
    showProgressBar: true,
    titleColor: "rgb(55, 65, 81)"
  },
  "touched-incomplete": {
    circleClasses: "border-2",
    titleClasses: "",
    circleStyle: {
      backgroundColor: "rgb(255, 243, 224)",
      borderColor: "rgb(255, 224, 178)",
      color: "rgb(230, 81, 0)"
    },
    showProgressBar: true,
    titleColor: "rgb(156, 163, 175)"
  },
  pending: {
    circleClasses: "border-2",
    titleClasses: "",
    circleStyle: {
      backgroundColor: "rgb(255, 255, 255)",
      borderColor: "rgb(224, 224, 224)",
      color: "rgb(158, 158, 158)"
    },
    statusText: "Pending",
    showProgressBar: false,
    titleColor: "rgb(156, 163, 175)"
  }
};
type StepState = keyof typeof STEP_STATE_CONFIG;
interface ProgressStepItemProps {
  step: {
    id: number;
    title: string;
    key: string;
  };
  currentStep: number;
  data: Record<string, unknown>;
  info: Record<string, unknown>;
  rawUnitId?: string | null;
  sellerUnitId?: string | null;
  isLastStep: boolean;
  onStepClick?: (stepNumber: number) => void;
  urlGroupId?: string | null;
}
export function ProgressStepItem(t0) {
  const $ = _c(62);
  const {
    step,
    currentStep,
    data,
    info,
    rawUnitId,
    sellerUnitId,
    urlGroupId,
    isLastStep,
    onStepClick
  } = t0;
  let t1;
  if ($[0] !== info || $[1] !== step.key) {
    t1 = info && typeof info === "object" ? (info as Record<string, unknown>)[step.key] as Array<{
      name: string;
      required?: boolean;
      conditionalRequirement?: {
        dependsOn: string;
        whenValue?: unknown;
        whenExists?: boolean;
      };
    }> || [] : [];
    $[0] = info;
    $[1] = step.key;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const stepInfo = t1;
  let t2;
  if ($[3] !== data || $[4] !== stepInfo) {
    t2 = getStepPercent(data, stepInfo);
    $[3] = data;
    $[4] = stepInfo;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const progressInfo = t2;
  const isCurrent = step.id === currentStep;
  const isCompleted = progressInfo.percent === 100 && progressInfo.totalCount > 0;
  const requiredCompleted = progressInfo.requiredPercent === 100 && progressInfo.requiredTotalCount > 0;
  const hasAnyData = progressInfo.filledCount > 0;
  let t3;
  if ($[6] !== hasAnyData || $[7] !== isCompleted || $[8] !== isCurrent || $[9] !== requiredCompleted || $[10] !== sellerUnitId || $[11] !== step.id) {
    t3 = () => {
      if (isCompleted) {
        return "completed";
      }
      if (isCurrent && requiredCompleted) {
        return "current-required-done";
      }
      if (isCurrent && hasAnyData) {
        return "current-incomplete";
      }
      if (isCurrent && !hasAnyData) {
        return "current-incomplete";
      }
      if (sellerUnitId && step.id < 7) {
        return "completed";
      }
      if (requiredCompleted) {
        return "touched-required-done";
      }
      if (hasAnyData) {
        return "touched-incomplete";
      }
      return "pending";
    };
    $[6] = hasAnyData;
    $[7] = isCompleted;
    $[8] = isCurrent;
    $[9] = requiredCompleted;
    $[10] = sellerUnitId;
    $[11] = step.id;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const getStepState = t3;
  const stepState = getStepState();
  const config = STEP_STATE_CONFIG[stepState];
  let isClickable;
  if ($[13] !== currentStep || $[14] !== rawUnitId || $[15] !== sellerUnitId || $[16] !== step.id || $[17] !== urlGroupId) {
    if (currentStep === step.id) {
      isClickable = false;
    } else {
      if (sellerUnitId) {
        isClickable = step.id >= 7;
      } else {
        if (rawUnitId) {
          isClickable = step.id <= 7;
        } else {
          if (urlGroupId) {
            isClickable = step.id <= 3;
          } else {
            isClickable = step.id <= 1;
          }
        }
      }
    }
    $[13] = currentStep;
    $[14] = rawUnitId;
    $[15] = sellerUnitId;
    $[16] = step.id;
    $[17] = urlGroupId;
    $[18] = isClickable;
  } else {
    isClickable = $[18];
  }
  let t4;
  if ($[19] !== isClickable || $[20] !== onStepClick || $[21] !== step.id) {
    t4 = () => {
      if (isClickable && onStepClick) {
        onStepClick(step.id);
      }
    };
    $[19] = isClickable;
    $[20] = onStepClick;
    $[21] = step.id;
    $[22] = t4;
  } else {
    t4 = $[22];
  }
  const handleStepClick = t4;
  const t5 = `flex flex-col relative ${isClickable ? "cursor-pointer hover:opacity-80" : ""}`;
  let t6;
  if ($[23] !== isLastStep) {
    t6 = !isLastStep && <div className="absolute left-4 top-8 w-px bg-neutral-200" style={{
      height: "calc(100% + 1rem)"
    }} />;
    $[23] = isLastStep;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  const t7 = `w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium relative z-10 ${config.circleClasses}`;
  let t8;
  if ($[25] !== isCompleted || $[26] !== step.id) {
    t8 = isCompleted ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg> : step.id;
    $[25] = isCompleted;
    $[26] = step.id;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  let t9;
  if ($[28] !== config.circleStyle || $[29] !== t7 || $[30] !== t8) {
    t9 = <div className={t7} style={config.circleStyle}>{t8}</div>;
    $[28] = config.circleStyle;
    $[29] = t7;
    $[30] = t8;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  const t10 = `text-xs leading-tight ${config.titleClasses}`;
  let t11;
  if ($[32] !== config.titleColor) {
    t11 = {
      color: config.titleColor
    };
    $[32] = config.titleColor;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== step.title || $[35] !== t10 || $[36] !== t11) {
    t12 = <div className="flex-1"><p className={t10} style={t11}>{step.title}</p></div>;
    $[34] = step.title;
    $[35] = t10;
    $[36] = t11;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  let t13;
  if ($[38] !== t12 || $[39] !== t9) {
    t13 = <div className="flex items-center gap-3">{t9}{t12}</div>;
    $[38] = t12;
    $[39] = t9;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  let t14;
  if ($[41] !== config.showProgressBar || $[42] !== config.statusText || $[43] !== stepState) {
    t14 = config.statusText && !config.showProgressBar ? <p className="text-[10px] ml-11 mt-0.5" style={{
      color: stepState === "current-incomplete" || stepState === "current-required-done" ? "rgb(107, 114, 128)" : "rgb(156, 163, 175)"
    }}>{config.statusText}</p> : null;
    $[41] = config.showProgressBar;
    $[42] = config.statusText;
    $[43] = stepState;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  let t15;
  if ($[45] !== config.showProgressBar || $[46] !== progressInfo.filledCount || $[47] !== progressInfo.optionalFilledCount || $[48] !== progressInfo.optionalTotalCount || $[49] !== progressInfo.percent || $[50] !== progressInfo.requiredFilledCount || $[51] !== progressInfo.requiredPercent || $[52] !== progressInfo.requiredTotalCount || $[53] !== progressInfo.totalCount) {
    t15 = config.showProgressBar && progressInfo.totalCount > 0 && <div className="ml-11 mt-1 space-y-1"><div className="flex items-center gap-2"><div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{
          backgroundColor: "rgb(229, 231, 235)"
        }}><div className="h-full transition-all duration-300 ease-out" style={{
            width: `${progressInfo.percent}%`,
            background: progressInfo.percent === 100 ? "rgb(76, 175, 80)" : progressInfo.requiredPercent === 100 ? "linear-gradient(90deg, rgb(139, 195, 74) 0%, rgb(255, 152, 0) 100%)" : "rgb(255, 152, 0)"
          }} /></div><span className="text-[10px] font-medium min-w-[28px] text-right" style={{
          color: progressInfo.percent === 100 ? "rgb(76, 175, 80)" : progressInfo.requiredPercent === 100 ? "rgb(139, 195, 74)" : "rgb(255, 152, 0)"
        }}>{progressInfo.percent}%</span></div><div className="text-[9px] space-x-4 flex">{progressInfo.requiredTotalCount > 0 && <div className="flex justify-between" style={{
          color: progressInfo.requiredPercent === 100 ? "rgb(139, 195, 74)" : "rgb(107, 114, 128)"
        }}><span className="pr-1">Required: </span><span>{progressInfo.requiredFilledCount}/{progressInfo.requiredTotalCount}</span></div>}{progressInfo.optionalTotalCount > 0 && <div className="flex justify-between" style={{
          color: "rgb(107, 114, 128)"
        }}><span className="pr-1">Optional:</span><span>{progressInfo.optionalFilledCount}/{progressInfo.optionalTotalCount}</span></div>}{progressInfo.totalCount > 0 && <div className="flex justify-between" style={{
          color: "rgb(107, 114, 128)"
        }}><span className="pr-1">Total:</span><span>{progressInfo.filledCount}/{progressInfo.totalCount}</span></div>}</div></div>;
    $[45] = config.showProgressBar;
    $[46] = progressInfo.filledCount;
    $[47] = progressInfo.optionalFilledCount;
    $[48] = progressInfo.optionalTotalCount;
    $[49] = progressInfo.percent;
    $[50] = progressInfo.requiredFilledCount;
    $[51] = progressInfo.requiredPercent;
    $[52] = progressInfo.requiredTotalCount;
    $[53] = progressInfo.totalCount;
    $[54] = t15;
  } else {
    t15 = $[54];
  }
  let t16;
  if ($[55] !== handleStepClick || $[56] !== t13 || $[57] !== t14 || $[58] !== t15 || $[59] !== t5 || $[60] !== t6) {
    t16 = <div className={t5} onClick={handleStepClick}>{t6}{t13}{t14}{t15}</div>;
    $[55] = handleStepClick;
    $[56] = t13;
    $[57] = t14;
    $[58] = t15;
    $[59] = t5;
    $[60] = t6;
    $[61] = t16;
  } else {
    t16 = $[61];
  }
  return t16;
}